import { useCallback, useLayoutEffect, useState } from 'react';
import { DEFAULT_DOCK_POSITION, clampDockBottom, companionDockKey, parseDockPosition } from '@/lib/companion';
import type { DockPosition } from '@/lib/companion';

/** 卡片 64px 加上頭像探出上緣的 14px。夾住位置、決定報告泡泡放哪邊都用這個高度，和 companion.css 一致。 */
export const DOCK_HEIGHT = 78;

export interface DockViewport { width: number; height: number; edge: number }

/** 側欄右緣（data-companion-edge）以右才是小哨能放的範圍；手機沒有側欄，量到 0。 */
function measureViewport(): DockViewport {
  const root = document.documentElement;
  const aside = document.querySelector<HTMLElement>('[data-companion-edge]');
  return {
    width: root.clientWidth,
    height: root.clientHeight,
    edge: aside ? Math.max(0, Math.round(aside.getBoundingClientRect().right)) : 0,
  };
}

/** 電腦版小哨的位置：依帳號存在這台瀏覽器。視窗變小時只在顯示時夾回畫面內，不改存好的值。 */
export function useCompanionDock(account: string, active: boolean) {
  const key = companionDockKey(account);
  const [position, setPosition] = useState<DockPosition>(() => {
    try { return parseDockPosition(localStorage.getItem(key)); }
    catch { return DEFAULT_DOCK_POSITION; }
  });
  const [viewport, setViewport] = useState<DockViewport>(() => ({ width: innerWidth, height: innerHeight, edge: 0 }));

  // 畫面繪出前先量好側欄寬度，靠左放時才不會先閃到側欄底下
  useLayoutEffect(() => {
    if (!active) return;
    const root = document.documentElement;
    const update = () => {
      const next = measureViewport();
      root.style.setProperty('--companion-edge', `${next.edge}px`);
      setViewport((current) => (current.width === next.width && current.height === next.height && current.edge === next.edge
        ? current : next));
    };
    update();
    // 側欄在 768～1023px 可以展開或收起，寬度會變
    const aside = document.querySelector('[data-companion-edge]');
    const observer = new ResizeObserver(update);
    if (aside) observer.observe(aside);
    window.addEventListener('resize', update);
    return () => {
      observer.disconnect();
      window.removeEventListener('resize', update);
      root.style.removeProperty('--companion-edge');
    };
  }, [active]);

  const move = useCallback((next: DockPosition) => {
    setPosition(next);
    try { localStorage.setItem(key, JSON.stringify(next)); }
    catch { /* 瀏覽器不允許保存時，這次仍照新位置顯示。 */ }
  }, [key]);

  const bottom = clampDockBottom(position.bottom, viewport.height, DOCK_HEIGHT);
  return {
    side: position.side, bottom, viewport, move,
    isDefault: position.side === DEFAULT_DOCK_POSITION.side && bottom === DEFAULT_DOCK_POSITION.bottom,
  };
}
